import { isDisplayOptionsOddsType, type Console, type DateTimeFormat } from '@adflow/types';
import { fmtReference, fmtTextDate, formatOdds } from '@adflow/utils';

import { getPreviewDate, isDateDataPoint } from './components/previewConstants';

/**
 * Defaults already used when a template has no date/time display options.
 * Element options replace these first, then template options.
 */
export const PREVIEW_DATE_TIME_DEFAULTS: DateTimeFormat = {
  timeFormat: 'hour24',
  dayFormat: 'long',
  monthFormat: 'long',
  showTimezone: false,
  countdownFormatStyle: 'digital',
  countdownTimeGranularity: 'H_M_S',
};

const PREVIEW_ODDS_DEFAULTS = {
  type: 'decimalOdds' as const,
  stake: 10,
  currency: 'GBP',
  calculateDecimalPlaces: false,
};

export type PreviewOddsSettings = {
  type?: Console.OddsType | null;
  stake?: number | null;
  currency?: string | null;
  calculateDecimalPlaces?: boolean | null;
};

export type PreviewFormatContext = {
  locale: string;
  timeZone: string;
  /** Shared target instant for date keys that are not already a Date. */
  instant?: Date;
  elementDateTimeFormat?: DateTimeFormat | null;
  templateDateTimeFormat?: DateTimeFormat | null;
  elementOdds?: PreviewOddsSettings | null;
  templateOdds?: PreviewOddsSettings | null;
  fallbackOdds?: PreviewOddsSettings | null;
};

export type ResolvedPreviewOdds = {
  type: Console.OddsType;
  stake: number;
  currency: string;
  calculateDecimalPlaces: boolean;
};

export function resolvePreviewDateTimeFormat(
  elementDateTimeFormat?: DateTimeFormat | null,
  templateDateTimeFormat?: DateTimeFormat | null,
): DateTimeFormat {
  return elementDateTimeFormat || templateDateTimeFormat || PREVIEW_DATE_TIME_DEFAULTS;
}

export function resolvePreviewOdds(
  context: Pick<PreviewFormatContext, 'elementOdds' | 'templateOdds' | 'fallbackOdds'>,
): ResolvedPreviewOdds {
  const templateType = context.templateOdds?.type;
  const type =
    context.elementOdds?.type ||
    (templateType && isDisplayOptionsOddsType(templateType) ? templateType : null) ||
    PREVIEW_ODDS_DEFAULTS.type;

  const templateHasStakeAndCurrency = Boolean(
    context.templateOdds?.stake && context.templateOdds.currency,
  );
  const stakeSource =
    context.elementOdds ||
    (templateHasStakeAndCurrency ? context.templateOdds : null) ||
    context.fallbackOdds ||
    PREVIEW_ODDS_DEFAULTS;

  return {
    type,
    stake: stakeSource.stake ?? PREVIEW_ODDS_DEFAULTS.stake,
    currency: stakeSource.currency ?? PREVIEW_ODDS_DEFAULTS.currency,
    calculateDecimalPlaces:
      stakeSource.calculateDecimalPlaces ?? PREVIEW_ODDS_DEFAULTS.calculateDecimalPlaces,
  };
}

function isOddsTuple(value: unknown): value is readonly [number, number] {
  return (
    Array.isArray(value) &&
    value.length === 2 &&
    typeof value[0] === 'number' &&
    typeof value[1] === 'number'
  );
}

/**
 * Formats a preview date or odds value.
 * Returns undefined for team names and other non-format values so callers can
 * keep their existing placeholder behavior.
 */
export function formatPreviewDataPoint(
  key: string,
  value: unknown,
  context: PreviewFormatContext,
): string | undefined {
  const dateTimeFormat = resolvePreviewDateTimeFormat(
    context.elementDateTimeFormat,
    context.templateDateTimeFormat,
  );

  if (value instanceof Date || isDateDataPoint(key)) {
    const date = value instanceof Date ? value : context.instant ?? getPreviewDate();

    if (key === 'DATE' && dateTimeFormat.isTextFormat) {
      return fmtTextDate(
        date,
        context.locale,
        context.timeZone,
        dateTimeFormat.timeFormat === 'hour12',
      );
    }

    return fmtReference(date, context.locale, context.timeZone, key, dateTimeFormat);
  }

  if (isOddsTuple(value)) {
    const odds = resolvePreviewOdds(context);
    return formatOdds(
      [value[0], value[1]],
      odds.type,
      odds.stake,
      odds.currency,
      context.locale,
      undefined,
      odds.calculateDecimalPlaces,
    );
  }

  return undefined;
}
